<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-shapes">
        <i class="far fa-shapes"></i> Shape Price Types
      </Tab>
      <Tab tabid="tab-cutouts">
        <i class="far fa-border-style-alt"></i> Cutout List
      </Tab>
      <Tab tabid="tab-edgework-list">
        <i class="far fa-border-outer"></i> Edgework List
      </Tab>
      <Tab tabid="tab-edgework-config">
        <i class="far fa-border-outer"></i> Edgework Config
      </Tab>
      <Tab tabid="tab-guidelines">
        <i class="far fa-pencil-ruler"></i> Guidelines
      </Tab>
      <Tab tabid="tab-settings">
        <i class="far fa-cogs"></i> Settings
      </Tab>
      <Tab tabid="tab-activity-log">
        <i class="far fa-history"></i> Activity Log
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-shapes">
        <DataTable ref="shapePriceTypeDataTable" :columns="shapePriceTypeColumnList" defaultColumnOrderDir="ASC" :defaultColumnOrderIndex="2" :url="shapePriceTypeDataURL" :displayTools="true">
          <template v-slot:title>
            <ContentTitle>Shape Price Type List</ContentTitle>
            <ContentSubTitle>Shape price type configurations with rules and links to service inventory items</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)" @click="addShapePriceType"> Add Shape Price Type</ActionButton>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                {{row.data.name}}
              </td>
              <td>
                <IconBoolean :value="row.data.isActive" />
              </td>
              <td>
                {{row.data.straightEdgeCountLimit}}
              </td>
              <td>
                {{row.data.straightLineCutCountLimit}}
              </td>
              <td>
                {{row.data.perfectArcCountLimit}}
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="editShapePriceType(row.data)" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Edit shape</DropdownMenuItem>
                  <DropdownMenuItem @click="setShapePriceTypeActive(row.data, false)" icon="fa-times" v-if="row.data.isActive && userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Make Inactive</DropdownMenuItem>
                  <DropdownMenuItem @click="setShapePriceTypeActive(row.data, true)" icon="fa-check" v-if="!row.data.isActive && userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Make Active</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-cutouts">
        <DataTable ref="cutoutDataTable" :columns="cutoutColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="cutoutDataURL" :filterOptions="cutoutFilterOptions" :displayTools="true">
          <template v-slot:title>
            <ContentTitle>Cutout Stock List</ContentTitle>
            <ContentSubTitle>Links to service inventory items with rules for cutouts applied in the order editor</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)" @click="addCutoutConfig"> Add Cutout</ActionButton>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                {{row.data.name}}
              </td>
              <td>
                <LabelBadge :labelClass="`label-ordereditor-cutouttype-${row.data.type}`">{{formatLookupName(row.data.type, OrderEditorCutoutTypeLookup)}}</LabelBadge>
              </td>
              <td>
                {{formatLookupName(row.data.geometryShapeType, OrderEditorGeometryShapeTypeLookup)}}
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="editCutoutConfig(row.data)" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Edit cutout</DropdownMenuItem>
                  <DropdownMenuItem @click="deleteCutoutConfig(row.data)" icon="fa-trash-alt" v-if="userHasDeletePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Delete cutout</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-edgework-list">
        <DataTable ref="edgeworkDataTable" :columns="edgeworkColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="edgeworkDataURL" :displayTools="true">
          <template v-slot:title>
            <ContentTitle>Edgework List</ContentTitle>
            <ContentSubTitle>Edgework list available in the order editor</ContentSubTitle>
          </template>
          <template v-slot:tools>
            <ActionButton type="new" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)" @click="addEdgework"> Add Edgework</ActionButton>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                {{row.data.name}}
              </td>
              <td>
                <IconBoolean :value="row.data.isActive" />
              </td>
              <td>
                <LabelBadge :labelColour="row.data.highlightColour">{{row.data.name}}</LabelBadge>
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="editEdgework(row.data)" icon="fa-pencil" v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Edit edgework</DropdownMenuItem>
                  <DropdownMenuItem @click="setEdgeworkConfigActive(row.data, false)" icon="fa-times" v-if="row.data.isActive && userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Make Inactive</DropdownMenuItem>
                  <DropdownMenuItem @click="setEdgeworkConfigActive(row.data, true)" icon="fa-check" v-if="!row.data.isActive && userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)">Make Active</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-edgework-config">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration) && !edgeworkLink.editing" class="float-right">
              <DropdownMenuItem @click="edgeworkLink.editing = true" icon="fa-pencil">Edit Edgework Config</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Edgework Config</ContentTitle>
            <ContentSubTitle>Setup for default edgework per price group for edges that have no edgework set</ContentSubTitle>
          </div>
        </div>
        <Spinner v-if="loading" />
        <FormGroup :labelColumnSize="6" :form="edgeworkLink.form" v-show="!loading">
          <div class="row mt-2">
            <div class="col-12">
              <DefaultEdgeworkLinkEditor ref="defaultEdgeworkLinkEditor" :editing="edgeworkLink.editing" v-model="edgeworkLink.settings" />
            </div>
          </div>

          <FormSubmit v-if="edgeworkLink.editing" label="Update" :submitting="edgeworkLink.submitting" @submitted="submitEdgeworkDefaultSettings" @cancelled="cancelEdgeworkDefaultSettings" />
        </FormGroup>        
      </TabPage>


      <TabPage tabid="tab-guidelines">
        <div class="row">
          <div class="col-12">
            <ActionDropMenu v-if="!orderEditorGuidelines.editing && userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration)" class="float-right">
              <DropdownMenuItem @click="editOrderEditorGuidelines" icon="fa-pencil">Edit Guidelines</DropdownMenuItem>
            </ActionDropMenu>

            <ContentTitle>Order Editor Guidelines</ContentTitle>
            <ContentSubTitle>Guidelines required for glass panels within the order editor</ContentSubTitle>
          </div>
        </div>
        <FormGroup :form="orderEditorGuidelines.form">
          <div class="row mt-2">
            <div class="col-12">
              <OrderEditorGuidelineEditor v-model="orderEditorGuidelines.list" :editing="orderEditorGuidelines.editing" />
            </div>
          </div>
          <template v-if="orderEditorGuidelines.editing">
            <FormHorizontalRule />
            <FormSubmit label="Update" :submitting="orderEditorGuidelines.submitting" @submitted="submitEditorGuidelines" @cancelled="cancelEditingEditorGuidelines" />
          </template>
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration) && !generalConfig.editing" class="float-right">
              <DropdownMenuItem @click="generalConfig.editing = true" icon="fa-pencil">Edit Editor Config</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Order Editor Settings</ContentTitle>
            <ContentSubTitle>General settings for the Order Editor</ContentSubTitle>
          </div>
        </div>
        <Spinner v-if="loading" />
        <FormGroup :labelColumnSize="6" :form="generalConfig.form" v-show="!loading">
          <div class="row mt-2">
            <div class="col-6">
              <FormAccountSetting label="Annealed Splitting Service Stock" helpDefault="None">
                <template v-slot:helpContent>
                  <p>For stock-based panels, the splitting tool is available to split the stock sheet.<br />
                  For each split on annealed sheets, this stock item will be inserted as a service under the order item in the order.<br />
                  If not set, no splitting service item will be inserted under the order item.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing && !getConfigSettingInt(AccountSettingKeys.annealedSplittingStockId)">None</div>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.annealedSplittingStockId)" :name="getLookupName(serviceStockListLookup, getConfigSettingInt(AccountSettingKeys.annealedSplittingStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :stockType="InventoryStockTypes.glassService" :value="getConfigSettingInt(AccountSettingKeys.annealedSplittingStockId)" @input="setConfigSetting(AccountSettingKeys.annealedSplittingStockId, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Laminate Splitting Service Stock" helpDefault="None" class="mt-4">
                <template v-slot:helpContent>
                  <p>For stock-based panels, the splitting tool is available to split the stock sheet.<br />
                  For each split on laminated sheets, this stock item will be inserted as a service under the order item in the order.<br />
                  If not set, no splitting service item will be inserted under the order item.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing && !getConfigSettingInt(AccountSettingKeys.laminateSplittingStockId)">None</div>
                  <LinkStock v-if="!generalConfig.editing" :id="getConfigSettingInt(AccountSettingKeys.laminateSplittingStockId)" :name="getLookupName(serviceStockListLookup, getConfigSettingInt(AccountSettingKeys.laminateSplittingStockId))" />
                  <FormInputStockCode v-if="generalConfig.editing" :stockType="InventoryStockTypes.glassService" :value="getConfigSettingInt(AccountSettingKeys.laminateSplittingStockId)" @input="setConfigSetting(AccountSettingKeys.laminateSplittingStockId, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Annealed Splitting - Max Split Count" helpDefault="2" class="mt-4">
                <template v-slot:helpContent>
                  <p>For stock-based panels, the splitting tool is available to split the annealed stock sheet.<br />
                  This setting controls how many splits are available per sheet.<br />
                  If zero, the splitting tool is unavailable for annealed sheets.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.annealedSplittingMaxSplits)}}</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.annealedSplittingMaxSplits)" @input="setConfigSetting(AccountSettingKeys.annealedSplittingMaxSplits, $event)" addon="splits" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Laminate Splitting - Max Split Count" helpDefault="2" class="mt-4">
                <template v-slot:helpContent>
                  <p>For stock-based panels, the splitting tool is available to split the laminated stock sheet.<br />
                  This setting controls how many splits are available per sheet.<br />
                  If zero, the splitting tool is unavailable for laminated sheets.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.laminateSplittingMaxSplits)}}</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.laminateSplittingMaxSplits)" @input="setConfigSetting(AccountSettingKeys.laminateSplittingMaxSplits, $event)" addon="splits" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Max Snapping Cut Line Size" helpDefault="150mm" class="mt-4">
                <template v-slot:helpContent>
                  <p>The maximum size of the snapping cutting line for straight line cuts.<br />
                  Measured from the edge of the glass panel, perpendicular to the boundary of the source rectangle during the straight line cutting.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.orderEditorMaxSnappingCutLineSize)}}mm</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderEditorMaxSnappingCutLineSize)" @input="setConfigSetting(AccountSettingKeys.orderEditorMaxSnappingCutLineSize, $event)" addon="mm" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Minimum Corner Radius Cutting" helpDefault="5mm" class="mt-4">
                <template v-slot:helpContent>
                  <p>The minimum radius of corner radiuses that will be cut at cutting.<br />
                  Corner radiuses below this value will not be cut and will be grinded down manually.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.orderEditorMinCuttingCornerRadius)}}mm</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderEditorMinCuttingCornerRadius)" @input="setConfigSetting(AccountSettingKeys.orderEditorMinCuttingCornerRadius, $event)" addon="mm" />
                </template>
              </FormAccountSetting>
            </div>

            <div class="col-6">
              <FormAccountSetting label="Editor Requires Review" helpDefault="False" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value will require the order items in the order editor to be individually reviewed before creating or updating the order<br />
                  If disabled, the review process is not required.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorRequiresReview)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorRequiresReview)" @change="setConfigSetting(AccountSettingKeys.orderEditorRequiresReview, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Editor Review - By Item" helpDefault="False" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value will split up the order editor review to individual items to be ticked off rather than ticking off groups.<br />
                  Ignored if the editor review is disabled.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorReviewByItem)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorReviewByItem)" @change="setConfigSetting(AccountSettingKeys.orderEditorReviewByItem, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Editor - Autosave Enabled" helpDefault="False" class="mt-4">
                <template v-slot:helpContent>
                  <p>Enabling this value will make the order editor auto-save the order every few minutes during order creation.<br />
                  If an issue occurs during order creation, new orders can be recovered via the order list.</p>
                </template>
                <template v-slot:entry>
                  <IconBoolean v-if="!generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorAutosaveEnabled)" />
                  <FormCheckbox v-if="generalConfig.editing" :value="getConfigSettingBoolean(AccountSettingKeys.orderEditorAutosaveEnabled)" @change="setConfigSetting(AccountSettingKeys.orderEditorAutosaveEnabled, $event)" />
                </template>
              </FormAccountSetting>

              <FormAccountSetting label="Editor - Autosave Interval" helpDefault="5 mins" class="mt-4">
                <template v-slot:helpContent>
                  <p>If order editor auto save is enabled, this is the interval in minutes of how often the order is auto saved to be available for recovery.<br />
                  Cannot be less than 2 minutes.</p>
                </template>
                <template v-slot:entry>
                  <div v-if="!generalConfig.editing">{{getConfigSettingInt(AccountSettingKeys.orderEditorAutosaveInterval)}} mins</div>
                  <FormInputText type="number" v-if="generalConfig.editing" :value="getConfigSettingInt(AccountSettingKeys.orderEditorAutosaveInterval)" @input="setConfigSetting(AccountSettingKeys.orderEditorAutosaveInterval, $event)" addon="mins" />
                </template>
              </FormAccountSetting>
            </div>
          </div>
          <FormSubmit v-if="generalConfig.editing" label="Update" :submitting="generalConfig.submitting" @submitted="submitGeneralConfigSettings" @cancelled="cancelGeneralConfigSettings" />
        </FormGroup>
      </TabPage>

      <TabPage tabid="tab-activity-log">
        <div class="row">
          <div class="col-lg-12">
            <ActivityLogStream ref="activityStream" url="/ordereditor/config/activitystream">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import Utils from '@/utils/utils'
import form from '@/mixins/form'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import DefaultEdgeworkLinkEditor from '@/modules/ordereditor/components/defaultEdgeworkLinkEditor'
import OrderEditorGuidelineEditor from '@/modules/ordereditor/components/orderEditorGuidelineEditor'

import ShapePriceTypeDialog from '@/modules/ordereditor/views/shapePriceTypeDialog'
import CutoutConfigDialog from '@/modules/ordereditor/views/cutoutConfigDialog'
import EdgeworkConfigDialog from '@/modules/ordereditor/views/edgeworkConfigDialog'

import { InventoryStockTypes, OrderEditorCutoutTypes, OrderEditorCutoutTypeLookup, OrderEditorGeometryShapeTypeLookup, AccountSettingKeys } from '@/store/types'

export default {
  name: 'OrderEditorConfiguration',
  mixins: [form],
  components: {
    FormInputStockCode,
    DefaultEdgeworkLinkEditor,
    OrderEditorGuidelineEditor
  },
  data: () => {
    return {
      loading: false,
      loadingError: null,
      stockList: [],
      generalConfig: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      },
      edgeworkLink: {
        editing: false,
        submitting: false,
        form: {},
        settings: []
      },

      shapePriceTypeDataURL: '/ordereditor/shapepricetypes?',
      shapePriceTypeColumnList: [
        { title: "Name", data: "name" },
        { title: "Active", data: "isActive" },
        { title: "Straight Edge Limit", data: "straightEdgeCountLimit" },
        { title: "Straight Line Cut Limit", data: "straightLineCutCountLimit" },
        { title: "Perfect Arc Limit", data: "perfectArcCountLimit" },
        { title: "Actions", data: "", sortable: false },
      ],      

      cutoutDataURL: '/ordereditor/cutouts?',
      cutoutColumnList: [
        { title: "Name", data: "name" },
        { title: "Type", data: "type" },
        { title: "Geometry Type", data: "geometryShapeType" },
        { title: "Actions", data: "", sortable: false },
      ],
      cutoutFilterOptions: [
        {
          field: 'type',
          name: 'Type',
          options: [
            { name: Utils.getLookupName(OrderEditorCutoutTypeLookup, OrderEditorCutoutTypes.radiusCut), value: OrderEditorCutoutTypes.radiusCut, class: 'label-ordereditor-cutouttype-radiusCut' },
            { name: Utils.getLookupName(OrderEditorCutoutTypeLookup, OrderEditorCutoutTypes.centerCutOut), value: OrderEditorCutoutTypes.centerCutOut, class: 'label-ordereditor-cutouttype-centerCutOut' },
            { name: Utils.getLookupName(OrderEditorCutoutTypeLookup, OrderEditorCutoutTypes.edgeCutOut), value: OrderEditorCutoutTypes.edgeCutOut, class: 'label-ordereditor-cutouttype-edgeCutOut' },
            { name: Utils.getLookupName(OrderEditorCutoutTypeLookup, OrderEditorCutoutTypes.cornerCutOut), value: OrderEditorCutoutTypes.cornerCutOut, class: 'label-ordereditor-cutouttype-cornerCutOut' },
          ]
        },
      ],

      edgeworkDataURL: '/ordereditor/edgeworks?',
      edgeworkColumnList: [
        { title: "Name", data: "name" },
        { title: "Active", data: "isActive" },
        { title: "Highlight", data: "highlightColour" },
        { title: "Actions", data: "", sortable: false },
      ],

      orderEditorGuidelines: {
        editing: false,
        submitting: false,
        list: [],
      },
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.OrderEditorCutoutTypeLookup = OrderEditorCutoutTypeLookup
    this.OrderEditorGeometryShapeTypeLookup = OrderEditorGeometryShapeTypeLookup
    this.AccountSettingKeys = AccountSettingKeys
  },
  mounted() {
    this.requestData()
  },
  computed: {
    serviceStockListLookup() {
      return this.$data.stockList.filter(stock => stock.type == InventoryStockTypes.glassService).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },    
  },
  methods: {
    reloadData() {
      this.reloadActivity()
      this.requestData()
      this.$refs.defaultEdgeworkLinkEditor.reloadData()
    },
    reloadActivity() {
      this.$refs.activityStream.reloadData()
    },
    requestData() {
      this.loadingError = false
      this.loading = true
      Promise.all([
        this.$apiService.orderEditorService.getConfigSettings(),
        this.$apiService.orderEditorService.getOrderEditorDefaultEdgeworkList(),
        this.$apiService.orderEditorService.getOrderEditorGuidelines(),
        this.$apiService.inventoryService.getStockList(),
      ])
      .then(results => {
        this.$data.generalConfig.settings = results[0].data
        this.$data.edgeworkLink.settings = results[1].data
        this.$data.orderEditorGuidelines.list = results[2].data
        this.$data.stockList = results[3].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })     
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.generalConfig.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseBoolean(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return parseInt(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.generalConfig.settings[settingKey] !== undefined)
        return this.parseDecimal(this.generalConfig.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.generalConfig.settings[settingKey] = value
    },

    submitGeneralConfigSettings() {
      this.$data.generalConfig.submitting = true
      this.$apiService.orderEditorService.updateConfigSettings({ settings: this.$data.generalConfig.settings })
      .then(response => {
        this.$data.generalConfig.editing = false
        this.successToast('General Config Settings updated')
      })
      .catch(err => {
        this.$data.generalConfig.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.generalConfig.submitting = false
      })
    },
    cancelGeneralConfigSettings() {
      this.$data.generalConfig.editing = false
      this.$apiService.orderEditorService.getConfigSettings()
      .then(response => {
        this.$data.generalConfig.settings = response.data
      })
    },

    addShapePriceType() {
      this.displayModal(ShapePriceTypeDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Shape Price Type Created')
          this.reloadActivity()
          this.$refs.shapePriceTypeDataTable.reload()
        }
      })
    },
    editShapePriceType(shapePriceType) {
      this.displayModal(ShapePriceTypeDialog, { 
        isCreateMode: false,
        id: shapePriceType.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Shape Price Type Updated')
          this.reloadActivity()
          this.$refs.shapePriceTypeDataTable.reload()
        }
      })
    },
    setShapePriceTypeActive(shapePriceType, isActive) {
      this.$apiService.orderEditorService.updateOrderEditorShapePriceType(shapePriceType.id, { isActive: isActive })
      .then(response => {
        this.successToast('Editor Shape Price Type Updated')
        this.reloadActivity()
        this.$refs.shapePriceTypeDataTable.reload()
      })
    },

    addCutoutConfig() {
      this.displayModal(CutoutConfigDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Cutout Created')
          this.reloadActivity()
          this.$refs.cutoutDataTable.reload()
        }
      })
    },
    editCutoutConfig(cutout) {
      this.displayModal(CutoutConfigDialog, { 
        isCreateMode: false,
        id: cutout.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Cutout Updated')
          this.reloadActivity()
          this.$refs.cutoutDataTable.reload()
        }
      })
    },
    deleteCutoutConfig(cutout) {
      this.confirmDeleteMessage('Delete Editor Cutout?', 'Are you sure you want to delete the cutout? This action cannot be undone!')
      .then(success => {
        if(success) {
          this.$apiService.orderEditorService.deleteOrderEditorCutout(cutout.id).then(response => {
            this.successToast('Editor Cutout Deleted')
            this.reloadActivity()
            this.$refs.cutoutDataTable.reload()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not delete cutout') })
        }
      })
    },

    addEdgework() {
      this.displayModal(EdgeworkConfigDialog, { 
        isCreateMode: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Edgework Created')
          this.reloadData()   // Reload all data to ensure the config list has updated edgeworks
          this.$refs.edgeworkDataTable.reload()
        }
      })
    },
    editEdgework(edgework) {
      this.displayModal(EdgeworkConfigDialog, { 
        isCreateMode: false,
        id: edgework.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Editor Edgework Updated')
          this.reloadData()   // Reload all data to ensure the config list has updated edgeworks
          this.$refs.edgeworkDataTable.reload()
        }
      })
    },
    setEdgeworkConfigActive(edgework, isActive) {
      this.$apiService.orderEditorService.updateOrderEditorEdgework(edgework.id, { isActive: isActive })
      .then(response => {
        this.successToast('Editor Edgework Updated')
        this.reloadData()   // Reload all data to ensure the config list has updated edgeworks
        this.$refs.edgeworkDataTable.reload()
      })
    },

    submitEdgeworkDefaultSettings() {
      this.edgeworkLink.submitting = true

      let updateData = {
        edgeworkLinkList: this.edgeworkLink.settings
      }
      this.$apiService.orderEditorService.updateOrderEditorDefaultEdgeworkList(updateData)
      .then(response => {
        this.edgeworkLink.editing = false
        this.successToast('Editor Edgework List Updated')
      })
      .catch(err => {
        this.edgeworkLink.form = this.processFormResponse(err)        
      })
      .finally(() => {
        this.edgeworkLink.submitting = false
      })      
    },
    cancelEdgeworkDefaultSettings() {
      this.edgeworkLink.editing = false
      this.edgeworkLink.submitting = false
      this.reloadData()
    },

    editOrderEditorGuidelines() {
      this.orderEditorGuidelines.editing = true
      this.$data.orderEditorGuidelines.form = this.clearFormResponse()
    },
    cancelEditingEditorGuidelines() {
      this.orderEditorGuidelines.editing = false
      this.reloadData()
    },
    submitEditorGuidelines() {
      this.orderEditorGuidelines.submitting = true
      this.$data.orderEditorGuidelines.form = this.clearFormResponse()

      var updateData = {
        orderEditorGuidelines: this.orderEditorGuidelines.list.map(guideline => {
          var outputData = _.cloneDeep(guideline)
          if(guideline.isNew)
            delete outputData.id
          return outputData
        })
      }

      this.$apiService.orderEditorService.updateOrderEditorGuidelines(updateData)
      .then((response) => {
        this.orderEditorGuidelines.editing = false
        this.successToast('Order Editor Guidelines Updated')
        this.reloadData()
      })
      .catch(err => {
        this.$data.orderEditorGuidelines.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.orderEditorGuidelines.submitting = false
      })
    },
  }
}

</script>